import { useEffect, useRef } from "react";

// Cloudflare Turnstile (anty-bot). Ładuje skrypt i renderuje widget przez explicit API,
// bez dodatkowej zależności npm. Token oddajemy przez onToken; jest jednorazowy —
// po wysyłce rodzic remontuje komponent (zmiana propa key), żeby dostać świeży token.

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
const loadScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Nie udało się załadować Turnstile"));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

interface Props {
  siteKey: string;
  onToken: (token: string | null) => void;
  theme?: "auto" | "light" | "dark";
  // "interaction-only" = widget pokazuje się tylko gdy potrzebna jest interakcja;
  // przy automatycznym powodzeniu pozostaje niewidoczny (token i tak wraca callbackiem).
  appearance?: "always" | "execute" | "interaction-only";
}

const Turnstile = ({
  siteKey,
  onToken,
  theme = "auto",
  appearance = "interaction-only",
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        if (widgetId.current !== null) return; // już wyrenderowany
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          appearance,
          callback: (token: string) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* ignore */
        }
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} />;
};

export default Turnstile;
